import { createSelector } from '@reduxjs/toolkit';
import type { WaykeQueryLocation } from '../@types/WaykeLocation';
import { parsers } from '../helpers';
import type { RootState } from '../reducers';
import { getPhoneParams, location as locationUtil } from '../utils';
import { type GetGeoLocation, getGeoLocation, getQueryFilter } from '../utils/reselectors';
import { getItem, getItemCentralStorageBranches } from './item';
import { getRetailerDocument, getRetailerPromos } from './retailer';
interface GetItemRetailerContactOptions {
  avatarUrl?: string | null;
  displayName?: string | null;
  id?: string | null;
  telephone?: {
    e164?: string | undefined;
    national?: string | undefined;
    international?: string | undefined;
  } | null;
  title?: string | null;
  email?: string | null;
}

export const getItemRetailerContactOptions = createSelector(
  [getItem, getRetailerDocument],
  (item, retailer): GetItemRetailerContactOptions | null => {
    if (!item) return null;

    const c1 = item?.contactOptions;
    const c2 = retailer?.defaultContactOptions;

    return {
      avatarUrl: c1?.avatarUrl || c2?.avatarUrl,
      displayName: c1?.displayName || c2?.displayName,
      id: c1?.id || c2?.id,
      telephone: parsers.parsePhoneNumber(
        c1?.telephone ||
          c2?.telephone ||
          (retailer && retailer.telephoneNumber ? retailer.telephoneNumber : null),
      ),
      title: c1?.title || c2?.title,
      email: c1?.email || c2?.email,
    };
  },
);

export const getItemRetailerPhoneParams = createSelector(
  [getItem, getRetailerDocument],
  (item, retailer): ((params: any) => void) => {
    return getPhoneParams.bind(null, item, retailer);
  },
);

type GetLocation = WaykeQueryLocation | GetGeoLocation;

export const getLocation = createSelector(
  [getQueryFilter, getGeoLocation],
  (queryFilter, geoLocation): GetLocation | null => {
    if (queryFilter.hasSpatial()) {
      return queryFilter.getSpatial();
    } else if (geoLocation) {
      return geoLocation;
    }
    return null;
  },
);

export const getItemCentralStorageBranchesLocationSorted = (state: RootState) => {
  const branches = getItemCentralStorageBranches(state);
  const location = getLocation(state);
  if (branches) {
    return branches
      .map((x) => {
        return {
          ...x,
          distance: location
            ? locationUtil.getDistance(
                location.lat,
                location.lng,
                x.position.location.lat,
                x.position.location.lon,
              )
            : null,
        };
      })
      .sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }
  return null;
};

export const getPromos = (state: RootState) => {
  return getRetailerPromos(state);
};
